import {PolygonGrid} from './polygon-grid';
import {PolygonPoint} from './polygon-point';
import {PolygonTitleLabel} from './polygon-title.label';
import {Label} from './label';
import {PolygonShape} from '../enums/polygon-shape.enum';
import {GeometryType} from '../enums/geometry-type.enum';
import {BaseGeometry} from './base-geometry';
import {GeometryConverter} from '../utils/geometry-converter';
import {PolyUtil} from '../utils/poly-util';
import {UnitConverter} from '../utils/unit-converter';
import {SettingsService} from '../services/settings.service';
import {SphericalUtil} from '../utils/spherical-util';
import {ParcelInfo} from './parce-info';
import {PolygonDistanceLabel} from './polygon-distance.label';
import {UserInfo} from '../../common/model/user-info';
import {Logger} from '../../common/utils/logger';
import {CommonUtil} from '../../common/utils/common.util';
import {FirebaseApi} from '../../common/services/firebase-api.service';
import {LineStyle} from "../enums/line-style.enum";
import {LineStyleManager} from "./line-style-manager";
import {AltitudeMode3d} from '../enums/altitude-mode.enum';
import type {BufferParams} from '../utils/buffer-util';

export class Polygon extends BaseGeometry {
  public readonly type = GeometryType.TYPE_POLYGON;
  description = '';
  parentId: string;
  groupId = '';

  points: Array<PolygonPoint> = [];
  path: Array<google.maps.LatLngLiteral> = [];
  linePath: Array<google.maps.LatLngLiteral> = [];
  paths: Array<Array<google.maps.LatLngLiteral>> = [];
  holes: Array<Polygon> = [];
  buffers: Array<Polygon> = [];

  distanceLabels: Array<PolygonDistanceLabel>;
  // Stats
  area = 0.0;
  perimeter = 0.0;

  // Settings
  lineWidth = 0;
  lineStyleManager = new LineStyleManager();
  lineStyle: LineStyle = LineStyle.SOLID;
  lineColor: string;
  fillColor: string;
  fillOpacity = 1.0;
  /**
   * Degrees to turn the assigned product's texture within this polygon.
   *
   * Per polygon rather than per product because it describes the ROOF, not the
   * material: every plane of a hip roof runs its courses in a different
   * direction, and they are all the same shingle. The tile SIZE stays on the
   * product, since that is a fact about the material.
   */
  textureRotation = 0;

  // ── 3D view ────────────────────────────────────────────────────────
  // Only meaningful on the 3D map; the flat map puts everything on the ground.
  // Each of these falls back to the project default until someone sets it on
  // the shape, so a project can be configured once and individual roofs
  // adjusted where they differ.

  /** See AltitudeMode3d. DEFAULT means "whatever the project says". */
  altitudeMode3d: string = AltitudeMode3d.DEFAULT;
  /**
   * Height in metres, used when the mode is FIXED.
   *
   * For a roof this is the eaves or ridge height — the shape is then drawn as
   * one flat plane at that height instead of draped over the building, which
   * is what a roof actually is.
   */
  altitude3d = 0;
  /**
   * Metres the whole shape -- fill, outline, pins and labels -- is moved up
   * (or, below zero, down) from where its mode puts it, corners with a height
   * of their own included. For the photorealistic surface, which is not
   * always where the building is: a shape traced on it can float a little
   * above a roof or sink into it.
   */
  heightOffset3d = 0;
  /** How far heightOffset3d goes either way, in metres: the panel's field and the 3D view's arrow. */
  static readonly MAX_HEIGHT_OFFSET_M = 100;
  /** Raise the shape into a solid block of its own height. */
  extruded3d = false;
  /** Cells laid over the shape (see PolygonGrid); null when it has none. */
  grid: PolygonGrid | null = null;
  showSurface = true;
  isLocked = false;
  showMiddleDistances = true;
  showPins = false;
  showPinsWhenUnlocked = false;
  showName = true;
  showArea = true;
  showPerimeter = true;
  showPriceLabel = false;
  isHole = false;

  /** Formatted product price for this polygon, shown on the label when
   *  showPriceLabel is on. Computed lazily via the provider below so the model
   *  doesn't depend on ProductsService. */
  priceText = '';
  /** Set by ProductsService at startup; returns the formatted price for a polygon. */
  static computePriceText: ((polygon: Polygon) => string) = null;

  isBuffer = false;
  /**
   * How the buffer this shape is (a piece of) was drawn, and round which
   * shape: set while it still follows that shape. Cleared when it is edited by
   * hand, after which it is a shape like any other. An inner buffer has this
   * without isBuffer: it is an area of its own, not a band cut round its shape.
   */
  bufferParams: BufferParams | null = null;
  label: PolygonTitleLabel;
  /**
   * Where the title label was put by hand (dragged there, or placed on
   * purpose, as a buffer's is). It stays there when the outline is edited.
   * Unset while the label sits in the middle of the shape and follows it.
   */
  private titlePosition: google.maps.LatLngLiteral;
  /**
   * A stored label position nobody said was placed by hand: one that sat in
   * the middle when it was saved, one saved before that was recorded, one
   * moved in the mobile apps or one read from a file. Every load used to pin
   * it, so a label that had only ever sat in the middle stayed behind when
   * the shape was edited after a reload. It is kept while the corners are the
   * ones it was stored with, so nothing moves on loading, and dropped once
   * they change, when the label follows the shape again.
   */
  private storedTitle: {position: google.maps.LatLngLiteral, corners: google.maps.LatLngLiteral[]};

  center: google.maps.LatLngLiteral;

  currentUser: UserInfo;

  isDownloadingUser = false;

  lastUserUpdateId: string;

  parcelInfo: ParcelInfo;

  set showPolygonTitleLabelBackground(show: boolean) {
    this.label.hasBackground = show;
  }

  constructor(id: string,
              public shape: PolygonShape) {
    super(id, GeometryType.TYPE_POLYGON);
    this.showMiddleDistances = SettingsService.settings.showDistanceLabel;
    this.label = new PolygonTitleLabel(this.id);
  }

  addPoint(point: PolygonPoint) {
    point.index = this.points.length;
    this.points.push(point);
    if (this.shape === PolygonShape.RECTANGLE && this.points.length > 3) {
      this.shape = PolygonShape.POLYGON;
    }
    this.update();
  }

  insertPoint(point: PolygonPoint, index: number) {
    this.points.splice(index, 0, point);
    if (this.shape === PolygonShape.RECTANGLE && this.points.length > 3) {
      this.shape = PolygonShape.POLYGON;
    }
    this.updatePointIndexes();
    this.update();

  }

  /**
   * Moves a point. A closed shape's first point and its closing copy are one
   * corner, so whichever of the two moves, the other goes with it, heights
   * included: a rectangle (which keeps its closing copy while unlocked) moved
   * by its first corner left the copy behind, opening the outline with a
   * spike back to where the corner had stood, and saved it like that.
   */
  movePoint(point: PolygonPoint, newLatLng: google.maps.LatLngLiteral) {
    // Before the move: afterwards the two ends no longer match.
    const closed = this.points.length > 2 && this.isClosed();
    if (this.shape === PolygonShape.RECTANGLE && this.points.length > 3) {
      // The closing copy is the first corner again, and the side leaving it
      // is the first corner's: its list neighbour is that same corner.
      const corner = closed && point === this.getLastPoint() ? this.getFirstPoint() : point;
      const next = corner.next;
      const side2Distance = SphericalUtil.computeDistanceBetween(corner, next);
      const heading = SphericalUtil.computeHeading360(corner, next);
      corner.move(newLatLng);
      const nextPointPosition = SphericalUtil.computeOffset(corner.latLng, side2Distance, heading);
      next.move(nextPointPosition);
      if (closed) {
        this.moveClosingTwin(corner);
        this.moveClosingTwin(next);
      }
    } else {
      point.move(newLatLng);
      if (closed) {
        this.moveClosingTwin(point);
      }
    }
    this.update();
  }

  /**
   * Puts the other end of a closed shape where `moved` now is, with its
   * heights: the first point when `moved` is the closing copy, the closing
   * copy when it is the first point. Any other point has no twin.
   */
  private moveClosingTwin(moved: PolygonPoint) {
    const first = this.getFirstPoint();
    const closing = this.getLastPoint();
    if (first === closing) {
      return;
    }
    const twin = moved === first ? closing : moved === closing ? first : null;
    if (twin) {
      twin.move(moved.latLng);
      twin.altitude = moved.altitude;
      twin.altitude3d = moved.altitude3d;
    }
  }

  removePoint(point: PolygonPoint) {
    const index = this.points.indexOf(point);
    Logger.log('removePoint: ', index)
    if (index >= 0) {
      this.points.splice(index, 1);
      if (this.shape === PolygonShape.RECTANGLE && this.points.length > 3) {
        this.shape = PolygonShape.POLYGON;
      }
      this.updatePointIndexes();
      this.update();
    }
  }

  equals(other: Polygon) {
    return this.id === other.id;
  }

  getPointsCount() {
    return this.points.length;
  }

  hasPoints() {
    return this.getPointsCount() > 0;
  }

  getFirstPoint() {
    return this.points[0];
  }

  getLastPoint() {
    return this.points[this.points.length - 1];
  }

  isClosed(): boolean {
    if (this.getPointsCount() > 0) {
      return this.getFirstPoint().equalsPosition(this.getLastPoint());
    }
    return false;
  }

  /**
   * Whether closing it would make an area: three distinct corners at least.
   * Closing one or two only repeated the first corner, which gave a shape
   * with no area that the old app could not even load. A circle is never
   * closed by adding a point.
   */
  canClose(): boolean {
    const n = this.getPointsCount();
    const corners = n > 1 && this.isClosed() ? n - 1 : n;
    return this.shape !== PolygonShape.CIRCLE && corners >= 3;
  }

  /**
   * A locked area whose points do not end on the first one. Locking closes a
   * drawn area by adding that point, but areas made from parcels, splits and
   * hole cuts arrived without it: their outline was drawn with a side
   * missing, that side had no length label, and the perimeter left it out.
   * They are closed here, as they are drawn filled.
   */
  closesImplicitly(): boolean {
    return this.isLocked && this.measuresAsClosed();
  }

  /**
   * An area whose points do not end on the first one, locked or not: its
   * perimeter takes in the side back to the first point. The area is always
   * measured closed, and so is the exported perimeter; an unlocked area used
   * to report its perimeter without that side (30 m for a 10 m square) while
   * the export said 40 m. Only the drawn outline and its side labels wait
   * for the lock (closesImplicitly).
   */
  measuresAsClosed(): boolean {
    return this.showSurface && this.shape !== PolygonShape.CIRCLE
      && this.points.length > 2 && !this.isClosed();
  }

  /**
   * Whether this shape has an area to measure: an area, or a hole or buffer,
   * whose area counts whatever the shape they belong to is. A line has none.
   */
  hasArea(): boolean {
    return this.showSurface !== false || this.isHole || this.isBuffer;
  }

  precalculateMove(pivotPoint: PolygonPoint, fromPoint: google.maps.LatLngLiteral, toPos: google.maps.LatLngLiteral): google.maps.LatLngLiteral[] {
    const pointsCount = this.getPointsCount();
    if (pointsCount > 0) {
      let newPoints: google.maps.LatLngLiteral[] = [];

      const toLat = toPos.lat;
      const toLng = toPos.lng;
      if (this.shape !== PolygonShape.CIRCLE) {
        const offX = toPos.lat - fromPoint.lat;
        const offY = toPos.lng - fromPoint.lng;
        let pivotId = '-2';
        if (pivotPoint) {
          pivotId = pivotPoint.id;
        }
        this.points.forEach(ap => {
          if (ap.id === pivotId) {
            newPoints.push({
              lat: toLat,
              lng: toLng
            });
          } else {
            newPoints.push({
              lat: ap.latLng.lat + offX,
              lng: ap.latLng.lng + offY
            });
          }
        });
      } else {
        if (pointsCount > 1) {
          newPoints = SphericalUtil.getCirclePoints({
            lat: toLat,
            lng: toLng
          }, this.getCircleRadius());
        }
      }

      return newPoints;
    }
    return null;
  }

  setRectangle3dPoint(p3: google.maps.LatLngLiteral) {
    if (this.shape === PolygonShape.RECTANGLE) {
      const first = this.getFirstPoint();
      const p1 = first.latLng;
      const p2 = first.next.latLng;
      const p33 = SphericalUtil.compute3rdPointInRectangle(p1, p2, p3);
      const p4 = SphericalUtil.computeFourthRectanglePoint(p1, p2, p33);
      this.setRectanglePoints(p1, p2, p33, p4);
    }
  }

  private setRectanglePoints(p1: google.maps.LatLngLiteral,
                             p2: google.maps.LatLngLiteral,
                             p3: google.maps.LatLngLiteral,
                             p4: google.maps.LatLngLiteral) {
    if (this.shape === PolygonShape.RECTANGLE) {

      if (this.points.length > 0) {
        const ap = this.getFirstPoint();
        ap.move(p1);
      } else {
        this.points.push(this.newPoint(p1));
      }

      if (this.points.length > 1) {
        const ap = this.points[1];
        ap.move(p2);
      } else {
        this.points.push(this.newPoint(p2));
      }

      if (this.points.length > 2) {
        const ap = this.points[2];
        ap.move(p3);
      } else {
        this.points.push(this.newPoint(p3));
      }


      if (this.points.length > 3) {
        const ap = this.points[3];
        ap.move(p4);
      } else {
        this.points.push(this.newPoint(p4));
      }

      if (this.points.length > 4) {
        const ap = this.points[4];
        ap.move(p1);
      } else {
        this.points.push(this.newPoint(p1));
      }

      this.update();
    }
  }

  private newPoint(position: google.maps.LatLngLiteral) {
    return new PolygonPoint(FirebaseApi.newGeoId(), this, position);
  }

  getLabelTitlePosition(): google.maps.LatLngLiteral {
    if (this.titlePosition) {
      return this.titlePosition;
    }
    const stored = this.storedTitlePosition();
    if (stored) {
      return stored;
    }
    // A circle whose points are all deleted has no centre left to read.
    if (this.shape === PolygonShape.CIRCLE && this.hasPoints()) {
      return this.getFirstPoint().latLng;
    }
    // The centre updatePath just worked out: an update asked for it twice more.
    return this.center || PolyUtil.computeCenterPoint(this);
  }

  getCenterPoint() {
    return PolyUtil.computeCenterPoint(this)
  }

  contains(other: Polygon, outlineOnly = false): boolean {
    return PolyUtil.contains(this, other, outlineOnly);
  }

  updatePointIndexes() {
    this.points.forEach((value, index) => {
      value.index = index;
    });
  }

  recalculateStats() {
    let totalArea = 0;
    let totalPerimeter = 0;
    if (this.shape === PolygonShape.CIRCLE) {
      if (this.points.length > 1) {
        const r = SphericalUtil.computeDistanceBetween(this.points[0].latLng, this.points[1].latLng);
        totalArea = Math.PI * Math.pow(r, 2);
        totalPerimeter = 2 * Math.PI * r;
      }
    } else {
      // A line has no area: nothing shows or totals one, and the server's
      // export writes 0. Working it out read the track as a ring closed back
      // to its start, which crosses itself on most GPS traces and mowing
      // zigzags, and that self-crossing area is the slow kind: tens to
      // hundreds of milliseconds on every load, move and undo of a long line,
      // for a number thrown away. A hole or a buffer takes the surface
      // setting of the shape it belongs to, and its area is used.
      if (this.hasArea()) {
        totalArea = SphericalUtil.computeArea(this.path);
      }
      totalPerimeter = SphericalUtil.computeLength(
        this.measuresAsClosed() ? [...this.path, this.path[0]] : this.path);
    }

    if (totalArea) {
      for (let i = 1; i < this.paths.length; i++) {
        totalArea -= SphericalUtil.computeArea(this.paths[i]);
      }
    }

    this.area = totalArea;
    this.perimeter = totalPerimeter;
    this.updateLabel();
  }

  updateLabel(position?: google.maps.LatLngLiteral) {
    // if (!this.label) {
    //   this.label.updateLabelText(this.name, this.area, this.perimeter, this.showName, this.showArea, this.showPerimeter);
    // }
    if (Polygon.isPosition(position)) {
      this.titlePosition = position;
      this.storedTitle = null;
      this.label.latLng = position;
    } else {
      this.label.latLng = this.getLabelTitlePosition();
    }
    this.label.textSize = SettingsService.settings.titleLabelSize;
    if (this.isLocked) {
      // Recompute the price at render time so it's always current (incl. resize).
      if (this.showPriceLabel && Polygon.computePriceText) {
        this.priceText = Polygon.computePriceText(this) || '';
      }
      this.label.updateLabelText(this.name, this.area, this.perimeter, this.showName, this.showArea, this.showPerimeter, this.showPriceLabel, this.priceText);
    }
  }

  /**
   * The title label as stored with the shape. Only a label placed by hand
   * stays at its stored position; any other stays only until the corners
   * change, then follows the shape again (see storedTitle).
   *
   * Only true counts. The mobile apps share these documents and move a label
   * without knowing the flag, so a false saved earlier could sit beside a
   * position moved there since, which it would send back to the middle.
   */
  loadLabel(position: google.maps.LatLngLiteral, placedByHand: boolean | null | undefined) {
    this.titlePosition = null;
    this.storedTitle = null;
    if (Polygon.isPosition(position)) {
      const copy = {lat: position.lat, lng: position.lng};
      if (placedByHand === true) {
        this.titlePosition = copy;
      } else {
        this.storedTitle = {position: copy, corners: this.cornerPositions()};
      }
    }
    this.updateLabel();
  }

  /** Whether the title label was placed by hand, which is saved with the shape. */
  titleLabelPlacedByHand(): boolean {
    return !!this.titlePosition;
  }

  /**
   * Takes the title label along when the whole shape is moved by an offset.
   * A label placed by hand keeps its place beside the shape; one in the
   * middle stays automatic. Setting it like a dragged label pinned every
   * moved shape's label, which then stayed behind when a corner was moved.
   */
  moveLabelBy(dLat: number, dLng: number) {
    const shift = (p: google.maps.LatLngLiteral) => ({lat: p.lat + dLat, lng: p.lng + dLng});
    if (this.titlePosition) {
      this.titlePosition = shift(this.titlePosition);
    } else if (this.storedTitle) {
      // The corners moved by the same offset (BasePoint.moveOffset adds it
      // the same way), so the stored label still belongs to them.
      this.storedTitle = {
        position: shift(this.storedTitle.position),
        corners: this.storedTitle.corners.map(shift)
      };
    }
    this.updateLabel();
  }

  /** The stored label position, while the corners are still the ones it was stored with. */
  private storedTitlePosition(): google.maps.LatLngLiteral {
    if (this.storedTitle && !this.hasCorners(this.storedTitle.corners)) {
      this.storedTitle = null;
    }
    return this.storedTitle ? this.storedTitle.position : null;
  }

  /** The corners' positions: the points without a closing point, so locking or unlocking is not an edit. */
  private cornerPositions(): google.maps.LatLngLiteral[] {
    const count = this.points.length > 1 && this.isClosed() ? this.points.length - 1 : this.points.length;
    return this.points.slice(0, count).map(p => ({lat: p.latLng.lat, lng: p.latLng.lng}));
  }

  private hasCorners(corners: google.maps.LatLngLiteral[]): boolean {
    const count = this.points.length > 1 && this.isClosed() ? this.points.length - 1 : this.points.length;
    if (count !== corners.length) {
      return false;
    }
    for (let i = 0; i < count; i++) {
      const p = this.points[i].latLng;
      if (p.lat !== corners[i].lat || p.lng !== corners[i].lng) {
        return false;
      }
    }
    return true;
  }

  /** A position with real coordinates: stored data without label fields gives {lat: undefined, lng: undefined}. */
  private static isPosition(position: google.maps.LatLngLiteral): boolean {
    return !!position && Number.isFinite(position.lat) && Number.isFinite(position.lng);
  }

  getPath(): google.maps.LatLngLiteral[] {
    if (this.shape === PolygonShape.CIRCLE && this.points.length > 1) {
      const first = this.getFirstPoint().latLng;
      const radius = SphericalUtil.computeDistanceBetween(first, this.points[1].latLng);
      return SphericalUtil.getCirclePoints(first, radius);
    }
    return this.points.map(point => point.latLng);
  }

  updatePath() {
    this.path = this.getPath();
    const paths = [this.path];
    if (this.holes && this.holes.length > 0) {
      const isClockwise = PolyUtil.isClockwise(this.path);
      this.holes.forEach(hole => {
        const holePath = hole.getPath();
        const isHoleClockwise = PolyUtil.isClockwise(holePath);
        if (isHoleClockwise === isClockwise) {
          holePath.reverse();
        }
        paths.push(holePath);
      });
    }
    this.paths = paths;
    this.linePath = this.points.map(point => point.latLng);
    if (this.closesImplicitly()) {
      this.linePath.push(this.points[0].latLng);
    }
    this.center = PolyUtil.computeCenterPoint(this);
  }

  calculateDistanceLabels(force: boolean = false) {
    const distances: PolygonDistanceLabel[] = [];
    const distancesLabelSize = SettingsService.settings.distanceLabelSize;
    // With the closing side of an area stored without its closing point.
    const sides = this.points.length - 1 + (this.closesImplicitly() ? 1 : 0);
    for (let i = 0; i < sides; i++) {
      const toIndex = (i + 1) % this.points.length;
      const labelInterface = this.distanceLabels ? this.distanceLabels[i] : null;
      let label: Label;
      if (labelInterface && !force) {
        label = labelInterface.label;
      } else {
        label = new Label('');
      }
      const fromPoint = this.points[i];
      const toPoint = this.points[toIndex];
      const from = fromPoint.latLng;
      const to = toPoint.latLng;
      const distance = SphericalUtil.computeDistanceBetween(from, to);
      label.textSize = distancesLabelSize;
      label.latLng = SphericalUtil.computeMidPoint(from, to);
      label.text = Polygon.sideLengthText(distance);
      label.n = distance;
      if (labelInterface && labelInterface.distance === distance && !force) {
        labelInterface.from = fromPoint;
        labelInterface.fromIndex = i;
        labelInterface.to = toPoint;
        labelInterface.toIndex = toIndex;
        labelInterface.label = label;
        distances.push(labelInterface);
      } else {
        distances.push({
          distance,
          label,
          from: fromPoint,
          fromIndex: i,
          to: toPoint,
          toIndex
        });
      }

      // const label = new Label('');
      // const fromPoint = this.points[i];
      // const toPoint = this.points[i + 1];
      // const from = fromPoint.latLng;
      // const to = toPoint.latLng;
      // const distance = SphericalUtil.computeDistanceBetween(from, to);
      // label.textSize = distancesLabelSize;
      // label.latLng = SphericalUtil.computeMidPoint(from, to);
      // label.text = UnitConverter.distanceToString(distance);
      // distances.push({
      //   distance,
      //   label,
      //   from,
      //   fromIndex: i,
      //   to,
      //   toIndex: i + 1
      // });
    }

    // Set once, synchronously. It used to be set again from a timer, which
    // cost a whole-app change detection per shape per call (thousands on
    // loading a large project) and could briefly bring back an older array.
    this.distanceLabels = distances;
  }

  /**
   * A side's length as its label on the map writes it. With the decimals
   * setting off, only the short unit (m, ft, yd, ken...) is written whole: the
   * long one keeps its decimals, as rounding it wrote a 1,499 m side as "1 km",
   * a 1,600 m one as "2 km" and a 1.4 mile one as "1 mile", off by up to half
   * a kilometre, mile or ri.
   */
  static sideLengthText(distance: number): string {
    const decimals = SettingsService.settings.showDistanceLabelDecimals
      || UnitConverter.getDistanceUnit(distance) !== UnitConverter.getDistanceUnit(distance, true);
    return UnitConverter.distanceToString(distance, decimals ? 2 : 0);
  }

  updatePointDescriptionLabels() {
    this.points.forEach(p => {
      p.updateLabel();
    });
  }

  update() {
    this.updatePath();
    this.updatePointIndexes();
    this.recalculateStats();
    this.calculateDistanceLabels();
    this.updateLabel();
  }

  toFirebaseData(deep?: boolean): any {
    return GeometryConverter.polygonToData(this, deep);
  }

  load(data: any) {
    GeometryConverter.loadDataToPolygon(this, data);
  }

  private getCircleRadius() {
    return SphericalUtil.computeDistanceBetween(this.getFirstPoint().latLng, this.getLastPoint().latLng);
  }

  getPolygonLineIcons(lineColor: string,  lineWidth: number) {
    return this.lineStyleManager.getPolygonLineIcons(this.lineStyle, lineColor, lineWidth);
  }

  getPolygonLineOpacity() {
    switch(this.lineStyle) {
      case LineStyle.DASHED:
      case LineStyle.DOTTED:
      case LineStyle.DASH_DOT:
        return 0.0;
      default:
        return 1.0;
    }
  }

  getPathLatLng(): google.maps.LatLng[] {
    const ps: google.maps.LatLng[] = [];
    if (this.shape === PolygonShape.CIRCLE) {
      this.path.forEach(p => {
        ps.push(new google.maps.LatLng(p));
      });
    } else {
      this.points.forEach(p => {
        ps.push(new google.maps.LatLng(p.latLng));
      });
    }
    return ps;
  }

  getBounds(): google.maps.LatLngBounds {
    const bounds = new google.maps.LatLngBounds();
    this.path.forEach(p => {
      bounds.extend(p)
    });
    return bounds
  }

  getTitle() {
    return this.name
    // return CommonUtil.escapeHtml(this.name);
  }


  getAreaText() {
    return UnitConverter.areaToString(this.area);
  }

  getPerimeterText() {
    return UnitConverter.distanceToString(this.perimeter)
  }

  toGPolygon(): google.maps.Polygon {
    return new google.maps.Polygon({
      paths: this.getPathLatLng(),
      geodesic: true,
    });
  }
}
